import React, { useEffect, useState } from "react";
import { Megaphone, Pencil, Plus, Trash2 } from "lucide-react";
import api from '../../api/axios';
import DateFilter from "../../components/dashboard/DateRangeFilter";
import { useToast } from "../../context/ToastContext";

export default function CampaignComponent({ role }) {
    const [campaigns, setCampaigns] = useState([]);
    const [formData, setFormData] = useState({
        camp_name: "",
        camp_start_date: "",
        camp_end_date: "",
        camp_status: "ACTIVE"
    });
    const [errors, setErrors] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);

    const [deleteId, setDeleteId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [search, setSearch] = useState("");

    const { success, error } = useToast();

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            setErrors(validate());
        }
    }, [formData]);

    // ===========================
    // FETCH CAMPAIGNS
    // ===========================
    const fetchCampaigns = async () => {
        try {
            const response = await api.get("/campaign/list");
            if (response?.data?.data) {
                setCampaigns(response.data.data);
            }
        } catch (error) {
            console.error("Fetch error:", error);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    const filteredCampaigns = campaigns.filter((campaign) =>
        campaign.camp_name
            ?.toLowerCase()
            .includes(search.toLowerCase())
    );

    // ===========================
    // HANDLE CHANGE
    // ===========================
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleDateChange = (key, e) => {
        setFormData({
            ...formData,
            [key]: e,
        });

    };

    // ===========================
    // VALIDATION
    // ===========================
    const validate = () => {
        let newErrors = {};

        if (!formData.camp_name.trim())
            newErrors.camp_name = "Campaign name is required";

        if (!formData.camp_start_date)
            newErrors.camp_start_date = "Start date is required";

        if (!formData.camp_end_date)
            newErrors.camp_end_date = "End date is required";

        if (!formData.camp_status)
            newErrors.camp_status = "Status is required";

        // ✅ FIXED DATE VALIDATION
        if (formData.camp_start_date && formData.camp_end_date) {
            const start = new Date(formData.camp_start_date);
            const end = new Date(formData.camp_end_date);

            if (end < start) {
                newErrors.camp_end_date = "End date must be after start date";
            }
        }
        return newErrors;
    };

    // ===========================
    // SUBMIT (CREATE / UPDATE)
    // ===========================
    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length !== 0) return;

        try {
            setLoading(true);

            const payload = {
                ...formData,
                camp_start_date: formData.camp_start_date
                    ? new Date(formData.camp_start_date).toISOString()
                    : null,
                camp_end_date: formData.camp_end_date
                    ? new Date(formData.camp_end_date).toISOString()
                    : null,
            };

            if (editingId) {
                await api.put(`/campaign/${editingId}`, payload);
                success("Campaign updated successfully");
            } else {
                await api.post(`/campaign/save`, payload);
                success("Campaign added successfully");
            }

            setFormData({
                camp_name: "",
                camp_start_date: "",
                camp_end_date: "",
                camp_status: "ACTIVE"
            });

            setEditingId(null);
            setOpenModal(false);
            fetchCampaigns();
        } catch (err) {
            if (err.response?.status === 409) {
                error(err.response.data.error);
            } else {
                error(err?.data?.message || 'failed to create campaign');
            }
        } finally {
            setLoading(false);
        }
    };

    // ===========================
    // EDIT
    // ===========================
    const handleEdit = (campaign) => {
        setFormData({
            camp_name: campaign.camp_name,
            camp_start_date: campaign.camp_start_date,
            camp_end_date: campaign.camp_end_date,
            camp_status: campaign.camp_status,
        });

        setEditingId(campaign.camp_id);
        setOpenModal(true);
    };

    // ===========================
    // DELETE
    // ===========================
    const handleDelete = async () => {
        if (!deleteId) return;

        try {
            setDeleteLoading(true);
            await api.delete(`/campaign/${deleteId}`);
            fetchCampaigns();
            setDeleteId(null);
            success("Campaign deleted successfully");
        } catch (error) {
            console.error("Delete error:", error);
        } finally {
            setDeleteLoading(false);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="py-0 sm:py-6 px-0 sm:px-6">

            <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                    <h1 className="premium-page-title">Campaigns</h1>
                    <p className="text-muted-foreground">Manage all your campaigns</p>
                </div>


                <button
                    className="dashboard-btn-new hover:-translate-y-px transition-transform duration-150 ease-in-out"
                    onClick={() => {
                        setEditingId(null);

                        setFormData({
                            camp_name: "",
                            camp_start_date: "",
                            camp_end_date: "",
                            camp_status: "ACTIVE",
                        });

                        setErrors({});
                        setOpenModal(true);
                    }}
                >
                    <span className="dashboard-btn-plus">
                        <Plus className="h-4 w-4 mr-1" />
                    </span> Create Campaign
                </button>
            </div>

            <div className="mx-auto grid md:grid-cols-1 gap-6 mt-5">

                {/* ================= FORM ================= */}
                {openModal && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">

                        <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden">

                            {/* Header */}

                            <div className="flex items-center justify-between border-b px-6 py-3">

                                <div className="flex items-center gap-3">

                                    {/* <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                                        <Megaphone className="h-6 w-6 text-blue-600" />
                                    </div> */}

                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ff69681a]">
                                        <Megaphone className="h-6 w-6 text-[#ff6968]" />
                                    </div>

                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">
                                            {editingId ? "Update Campaign" : "Create Campaign"}
                                        </h2>

                                        <p className="text-sm text-gray-500">
                                            Fill in the campaign details.
                                        </p>
                                    </div>

                                </div>

                                <button
                                    onClick={() => {
                                        setOpenModal(false);
                                        setEditingId(null);

                                        setFormData({
                                            camp_name: "",
                                            camp_start_date: "",
                                            camp_end_date: "",
                                            camp_status: "ACTIVE",
                                        });

                                        setErrors({});
                                    }}
                                    className="text-3xl leading-none text-gray-400 hover:text-red-500"
                                >
                                    ×
                                </button>

                            </div>

                            {/* Body */}

                            <form onSubmit={handleSubmit}>

                                <div className="space-y-6 p-6">

                                    <div>

                                        <label className="mb-2 block text-sm font-medium text-gray-700">
                                            Campaign Name
                                        </label>

                                        <input
                                            type="text"
                                            name="camp_name"
                                            placeholder="Enter campaign name"
                                            value={formData.camp_name}
                                            onChange={handleChange}
                                            // className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none"
                                            className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-[#ff696866] focus:bg-white focus:outline-none"
                                        />

                                        {errors.camp_name && (
                                            <p className="mt-1 text-xs text-red-500">
                                                {errors.camp_name}
                                            </p>
                                        )}

                                    </div>

                                    <div className="grid gap-5 md:grid-cols-2">

                                        <div>

                                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                                Start Date
                                            </label>

                                            <DateFilter
                                                className="w-full"
                                                name="camp_start_date"
                                                value={formData.camp_start_date}
                                                onDateChange={(date) =>
                                                    handleDateChange("camp_start_date", date)
                                                }
                                            />

                                            {errors.camp_start_date && (
                                                <p className="mt-1 text-xs text-red-500">
                                                    {errors.camp_start_date}
                                                </p>
                                            )}

                                        </div>

                                        <div>

                                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                                End Date
                                            </label>

                                            <DateFilter
                                                className="w-full"
                                                name="camp_end_date"
                                                value={formData.camp_end_date}
                                                onDateChange={(date) =>
                                                    handleDateChange("camp_end_date", date)
                                                }
                                            />

                                            {errors.camp_end_date && (
                                                <p className="mt-1 text-xs text-red-500">
                                                    {errors.camp_end_date}
                                                </p>
                                            )}

                                        </div>

                                    </div>

                                    <div>

                                        <label className="mb-2 block text-sm font-medium text-gray-700">
                                            Status
                                        </label>

                                        <div className="relative">
                                            <select
                                                name="camp_status"
                                                value={formData.camp_status}
                                                onChange={handleChange}
                                                className={`w-full appearance-none cursor-pointer rounded-xl border bg-white px-4 py-2.5 pr-10 text-sm text-slate-800 outline-none transition-all duration-200
                                                ${errors.camp_status
                                                        ? "border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                                                        : "border-slate-200 hover:border-[#ff696866] focus:border-[#ff6968] focus:ring-4 focus:ring-[#ff69681a]"
                                                    }`}

                                            >
                                                <option value="ACTIVE">Active</option>
                                                <option value="INACTIVE">Inactive</option>
                                            </select>

                                            {/* Dropdown Icon */}
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down 
                                                    absolute
                                                    right-3
                                                    top-1/2
                                                    -translate-y-1/2
                                                    text-gray-500
                                                    pointer-events-none" aria-hidden="true">
                                                <path d="m6 9 6 6 6-6"></path>
                                            </svg>
                                        </div>
                                        {errors.camp_status && (
                                            <p className="mt-1 text-xs text-red-500">
                                                {errors.camp_status}
                                            </p>
                                        )}

                                    </div>

                                </div>

                                {/* Footer */}

                                {/* <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setOpenModal(false);
                                            setEditingId(null);
                                        }}
                                        className="rounded-xl border border-gray-300 px-5 py-2.5 hover:bg-gray-100"
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2.5 text-white hover:bg-blue-700 disabled:opacity-60"
                                    >
                                        {loading ? (
                                            <>
                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            editingId ? "Update Campaign" : "Create Campaign"
                                        )}
                                    </button>

                                </div> */}

                                <div className="flex justify-end gap-3 border-t border-slate-200 bg-white/80 px-6 py-3">

                                    <button type="button"
                                        onClick={() => {
                                            setOpenModal(false);
                                            setEditingId(null);
                                        }}
                                        className=" rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-600 shadow-sm transition-all duration-200 hover:border-[#ff696866] hover:bg-[#ff69680d] hover:text-[#e85b5a] focus:outline-none focus:ring-4 focus:ring-[#ff69681a] "
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className=" flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#f43f5e] px-6 py-2.5 text-sm font-semibold text-white shadow-[0_6px_18px_rgba(255,105,104,0.22)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_9px_24px_rgba(255,105,104,0.28)] focus:outline-none focus:ring-4 focus:ring-[#ff69681a] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 "
                                    >
                                        {loading ? (
                                            <>
                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"> </span>
                                                Saving...
                                            </>
                                        ) : (
                                            editingId ? "Update Campaign" : "Create Campaign"
                                        )}
                                    </button>
                                </div>


                            </form>

                        </div>

                    </div>
                )}

                {/* ================= LIST ================= */}
                <div className="rounded-xl bg-white shadow">

                    {/* Header */}

                    <div className="flex flex-col gap-4 border-b p-6 md:flex-row md:items-center md:justify-between">

                        <input
                            type="text"
                            name="campaign_search"
                            placeholder="Search campaign..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            // className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none"
                            className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-[#ff696866] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ff69681a]"
                        />

                    </div>

                    {/* Table */}

                    <div className="overflow-x-auto">

                        <table className="min-w-full">

                            <thead className="bg-gray-50">

                                <tr>

                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                                        Campaign
                                    </th>

                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                                        Start Date
                                    </th>

                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                                        End Date
                                    </th>

                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                                        Status
                                    </th>

                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                                        Actions
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {filteredCampaigns.length > 0 ? (

                                    filteredCampaigns.map((campaign) => (

                                        <tr
                                            key={campaign.camp_id}
                                            className="border-t hover:bg-gray-50 transition"
                                        >

                                            <td className="px-6 py-5">

                                                <div className="flex items-center gap-4">

                                                    {/* <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">

                                                        <Megaphone
                                                            size={22}
                                                            className="text-blue-600"
                                                        />

                                                    </div> */}

                                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#ff69681a]">
                                                        <Megaphone size={22} className=" text-[#ff6968]" />
                                                    </div>

                                                    <div>

                                                        <h4 className="font-semibold text-gray-800">
                                                            {campaign.camp_name}
                                                        </h4>

                                                        <p className="text-xs text-gray-500">
                                                            Campaign
                                                        </p>

                                                    </div>

                                                </div>

                                            </td>

                                            <td className="px-6 py-5 text-sm text-gray-600">
                                                {new Date(
                                                    campaign.camp_start_date
                                                ).toLocaleDateString()}
                                            </td>

                                            <td className="px-6 py-5 text-sm text-gray-600">
                                                {new Date(
                                                    campaign.camp_end_date
                                                ).toLocaleDateString()}
                                            </td>

                                            <td className="px-6 py-5 text-center">

                                                <span
                                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${campaign.camp_status === "ACTIVE"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-red-100 text-red-700"
                                                        }`}
                                                >
                                                    {campaign.camp_status}
                                                </span>

                                            </td>

                                            <td className="px-6 py-5">

                                                <div className="flex justify-center gap-3">

                                                    <button
                                                        onClick={() => handleEdit(campaign)}
                                                        className="rounded-lg p-2 text-amber-600 hover:bg-amber-100 transition"
                                                        title="Edit Campaign"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>

                                                    <button
                                                        onClick={() =>
                                                            setDeleteId(campaign.camp_id)
                                                        }
                                                        className="rounded-lg p-2 text-red-600 hover:bg-red-100 transition"
                                                        title="Delete Campaign"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    ))

                                ) : (

                                    <tr>

                                        <td
                                            colSpan="5"
                                            className="py-12 text-center"
                                        >

                                            <Megaphone
                                                size={45}
                                                className="mx-auto mb-3 text-gray-300"
                                            />

                                            <h3 className="text-lg font-semibold text-gray-700">
                                                No Campaigns Found
                                            </h3>

                                            <p className="mt-2 text-sm text-gray-500">
                                                Create your first campaign to get started.
                                            </p>

                                        </td>

                                    </tr>

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>

            {deleteId && (
                // <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1111]">
                //     <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 animate-fadeIn">

                //         <h3 className="text-lg font-semibold mb-2">
                //             Delete Campaign
                //         </h3>

                //         <p className="text-sm text-gray-600 mb-6">
                //             Are you sure you want to delete this campaign?
                //             This action cannot be undone.
                //         </p>

                //         <div className="flex justify-end gap-3">
                //             <button
                //                 onClick={() => setDeleteId(null)}
                //                 className="px-4 py-2 rounded-lg border hover:bg-gray-100"
                //             >
                //                 Cancel
                //             </button>

                //             <button
                //                 onClick={handleDelete}
                //                 disabled={deleteLoading}
                //                 className="px-4 py-2 rounded-lg bg-red-600 text-white 
                //                hover:bg-red-700 flex items-center gap-2 
                //                disabled:opacity-70"
                //             >
                //                 {deleteLoading ? (
                //                     <>
                //                         <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                //                         Deleting...
                //                     </>
                //                 ) : (
                //                     "Delete"
                //                 )}
                //             </button>
                //         </div>
                //     </div>
                // </div>
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1111] p-4">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-fadeIn">

                        {/* Header */}
                        <div className="px-6 pt-6">
                            <div className="flex items-start gap-4">

                                {/* Delete Icon */}
                                <div className="flex-shrink-0 w-11 h-11 rounded-full bg-red-50 flex items-center justify-center">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-5 h-5 text-red-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14"
                                        />
                                    </svg>
                                </div>

                                {/* Title + Description */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Delete Campaign
                                    </h3>

                                    <p className="mt-1.5 text-sm leading-5 text-gray-500">
                                        Are you sure you want to delete this campaign?
                                        This action cannot be undone.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-gray-100 mt-6"></div>

                        {/* Actions */}
                        <div className="px-6 py-4 bg-gray-50/70 flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteId(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 
                           bg-white border border-gray-300 rounded-lg
                           hover:bg-gray-50 hover:border-gray-400
                           focus:outline-none focus:ring-2 focus:ring-gray-200
                           transition-all duration-200"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleDelete}
                                disabled={deleteLoading}
                                className="px-4 py-2 text-sm font-medium text-white 
                           bg-red-600 rounded-lg
                           hover:bg-red-700
                           focus:outline-none focus:ring-2 focus:ring-red-200
                           flex items-center justify-center gap-2
                           disabled:opacity-60 disabled:cursor-not-allowed
                           transition-all duration-200"
                            >
                                {deleteLoading ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14"
                                            />
                                        </svg>
                                        Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
